/**
 * Budget Service
 * Business logic for budget operations
 */

import { 
  API_ENDPOINTS, 
  makeAuthenticatedApiRequest, 
  type ApiResult, 
  type StoreBudgetRequest,
  type StoreBudgetResponse,
  type GetBudgetResponse,
  type DeleteBudgetCategoryResponse
} from './apiConfig';
import { validateBudgetItems, validateAccessToken, validateCategoryName, type BudgetItem } from './validators';

/**
 * Store user budget via API
 * Replaces any existing budget categories with the same names
 */
export async function storeBudget(
  budgetItems: BudgetItem[],
  accessToken: string
): Promise<ApiResult<StoreBudgetResponse>> {
  // Input validation
  validateBudgetItems(budgetItems);
  validateAccessToken(accessToken);
  
  const payload: StoreBudgetRequest = {
    items: budgetItems.map(item => ({
      name: item.name.trim(),
      amount: item.amount,
      kind: item.kind,
      currency: item.currency.toUpperCase()
    }))
  };
  
  return makeAuthenticatedApiRequest(API_ENDPOINTS.BUDGET.STORE, payload, accessToken);
}

/**
 * Get user budget via API
 * Retrieves all budget items for the authenticated user
 */
export async function getBudget(
  accessToken: string
): Promise<ApiResult<GetBudgetResponse>> {
  // Input validation
  validateAccessToken(accessToken);
  
  return makeAuthenticatedApiRequest(API_ENDPOINTS.BUDGET.GET, undefined, accessToken);
}

/**
 * Delete a budget category via API
 */
export async function deleteBudgetCategory(
  categoryName: string,
  accessToken: string
): Promise<ApiResult<DeleteBudgetCategoryResponse>> {
  // Input validation
  validateCategoryName(categoryName);
  validateAccessToken(accessToken);
  
  const endpoint = {
    ...API_ENDPOINTS.BUDGET.DELETE_CATEGORY,
    url: `${API_ENDPOINTS.BUDGET.DELETE_CATEGORY.url}/${encodeURIComponent(categoryName.trim())}`
  };
  
  return makeAuthenticatedApiRequest(endpoint, undefined, accessToken);
}